<template>
    <v-navigation-drawer :value="value" @input="$emit('input', $event)" temporary right fixed :width="drawerWidth">
        <div class="fd-head">
            <span class="fd-title">Filtros</span>
            <v-btn icon small aria-label="Cerrar filtros" @click="close">
                <v-icon small>fas fa-times</v-icon>
            </v-btn>
        </div>

        <div class="fd-body form-compact">
            <section class="fd-section">
                <h3 class="fd-label">Qué entró</h3>
                <v-autocomplete v-model="draft.productId" :items="products" item-text="nombre" item-value="id"
                    label="Producto" prepend-inner-icon="fas fa-drumstick-bite" filled outlined clearable
                    hide-details="auto" class="fd-field">
                </v-autocomplete>
                <v-autocomplete v-if="showSucursal" v-model="draft.sucursalId" :items="sucursales" item-text="nombre" item-value="id"
                    label="Sucursal que recibe" prepend-inner-icon="fas fa-store-alt" filled outlined clearable
                    hide-details="auto" class="fd-field">
                </v-autocomplete>
                <v-select v-model="draft.typeEntryIds" :items="tipoEntrada" item-text="nombre" item-value="id"
                    label="Tipo de entrada" prepend-inner-icon="fas fa-tags" multiple small-chips deletable-chips
                    filled outlined clearable hide-details="auto" class="fd-field">
                </v-select>
            </section>

            <section class="fd-section">
                <h3 class="fd-label">Estado</h3>
                <QuickViews v-model="draft.status" :views="statusViews" />
            </section>

            <section class="fd-section">
                <h3 class="fd-label">Mermas y existencia <span class="fd-hint">Deja vacío lo que no uses</span></h3>
                <div v-for="field in rangeFields" :key="field.key" class="fd-range">
                    <div class="fd-range__name">{{ field.label }}</div>
                    <div class="fd-range__inputs">
                        <v-text-field v-model="draft.ranges[field.key].min" label="Mínimo" type="number" min="0"
                            step="any" filled outlined hide-details="auto" :error="inverted(field.key)">
                        </v-text-field>
                        <v-text-field v-model="draft.ranges[field.key].max" label="Máximo" type="number" min="0"
                            step="any" filled outlined hide-details="auto" :error="inverted(field.key)">
                        </v-text-field>
                    </div>
                    <div v-if="inverted(field.key)" class="fd-error error--text">El mínimo no puede ser mayor que el máximo.</div>
                </div>
            </section>
        </div>

        <template v-slot:append>
            <div class="fd-footer">
                <v-btn text small @click="clear">Limpiar</v-btn>
                <v-btn color="primary" small depressed @click="apply">Aplicar filtros</v-btn>
            </div>
        </template>
    </v-navigation-drawer>
</template>
<script>
import QuickViews from '@/components/shared/QuickViews.vue';
import {
    STATUS_VIEWS, RANGE_FIELDS, emptyAdvanced, cloneAdvanced, hasInvertedRange, isRangeInverted
} from './entradaFilters';

/**
 * Panel lateral con los filtros avanzados de Entradas.
 *
 * Edita un borrador (`draft`) y solo manda el resultado con `apply`:
 * ListaEntrada lo recibe de una vez, así que aplicar 6 filtros es 1 consulta
 * y no 6. Al abrirse, el borrador se reinicia con lo que está aplicado.
 */
export default {
    name: 'EntradaFilterDrawer',
    components: { QuickViews },
    props: {
        value: { type: Boolean, default: false },
        // Lo aplicado hoy (objeto `advanced` de entradaFilters.js).
        filters: { type: Object, required: true },
        products: { type: Array, default: () => [] },
        sucursales: { type: Array, default: () => [] },
        tipoEntrada: { type: Array, default: () => [] },
        // Quien solo ve su sucursal no necesita este filtro.
        showSucursal: { type: Boolean, default: false }
    },
    data() {
        return {
            draft: cloneAdvanced(this.filters),
            statusViews: STATUS_VIEWS,
            rangeFields: RANGE_FIELDS
        };
    },
    computed: {
        drawerWidth() {
            return this.$vuetify.breakpoint.xsOnly ? '100%' : 380;
        }
    },
    watch: {
        value(open) {
            if (open) this.draft = cloneAdvanced(this.filters);
        }
    },
    methods: {
        inverted(key) {
            return isRangeInverted(this.draft.ranges[key]);
        },
        close() {
            this.$emit('input', false);
        },
        clear() {
            this.draft = emptyAdvanced();
        },
        apply() {
            // Con un rango invertido no se envía: el mensaje ya está a la vista.
            if (hasInvertedRange(this.draft)) return;
            this.$emit('apply', cloneAdvanced(this.draft));
            this.close();
        }
    }
}
</script>
<style scoped>
.fd-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid var(--app-line);
}
.fd-title {
    font-size: 16px;
    font-weight: 600;
}
.fd-body {
    padding: 8px 16px 16px;
}
/* El aire entre secciones es padding, no margin (DESIGN.md). */
.fd-section {
    padding-top: 16px;
}
.fd-label {
    font-size: 13px;
    font-weight: 700;
    color: var(--app-text-muted);
    padding-bottom: 8px;
}
.fd-hint {
    font-weight: 400;
    margin-left: 6px;
}
.fd-field {
    padding-bottom: 12px;
}
.fd-range {
    padding-bottom: 12px;
}
.fd-range__name {
    font-size: 13px;
    font-weight: 500;
    padding-bottom: 6px;
}
.fd-range__inputs {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 8px;
}
.fd-error {
    font-size: 12px;
    padding-top: 4px;
}
.fd-footer {
    display: flex;
    justify-content: space-between;
    padding: 12px 16px;
    border-top: 1px solid var(--app-line-strong);
}
</style>
